import React, { useState } from 'react';
import { Box, Typography, IconButton, Paper, Button, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import RestoreIcon from '@mui/icons-material/Restore';
import { format, isSameDay, isToday, isPast, startOfDay } from 'date-fns';
import CalendarDay from './CalendarDay';
import DateRangePicker from './DateRangePicker';

const WorkPlannerCalendar = ({
    workingDays,
    getTasksForDay,
    onTaskDrop,
    onPreviousWeek,
    onNextWeek,
    onDateRangeChange,
    onResetToCurrentWeek,
    selectedDate,
    customDateRange,
    scheduledTaskIds
}) => {
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const weekStart = workingDays[0];
    const weekEnd = workingDays[workingDays.length - 1];

    // Check if showing custom range
    const isCustomRange = customDateRange !== null;

    return (
        <Paper
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Header with Week Navigation */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={onPreviousWeek} size="small">
                        <ChevronLeftIcon />
                    </IconButton>

                    <Button
                        variant="outlined"
                        startIcon={<EventIcon />}
                        onClick={() => setDatePickerOpen(true)}
                        sx={{
                            borderRadius: '20px',
                            textTransform: 'none',
                            borderColor: '#D1D5DB',
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '14px',
                            // '&:hover': {
                            //     borderColor: '#9CA3AF',
                            //     backgroundColor: '#F9FAFB'
                            // }
                        }}
                    >
                        {format(weekStart, 'dd')}-{format(weekEnd, 'dd MMM yyyy')}
                    </Button>

                    <IconButton onClick={onNextWeek} size="small">
                        <ChevronRightIcon />
                    </IconButton>

                    {/* Custom Range Indicator */}
                    {isCustomRange && (
                        <Chip
                            label={`Custom Range (${workingDays.length} days)`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Box>

                {/* Reset to Current Week Button */}
                {isCustomRange && (
                    <Button
                        variant="text"
                        startIcon={<RestoreIcon />}
                        onClick={onResetToCurrentWeek}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#6B7280'
                        }}
                    >
                        Reset to Current Week
                    </Button>
                )}
            </Box>

            {/* Calendar Grid */}
            <Box
                sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: `repeat(${workingDays.length}, 1fr)`,
                    gap: 0,
                    overflow: 'auto',
                    backgroundColor: '#F9FAFB'
                }}
            >
                {workingDays.map((day) => (
                    <CalendarDay
                        key={day.toISOString()}
                        day={day}
                        tasks={getTasksForDay(day)}
                        onTaskDrop={onTaskDrop}
                        isToday={isToday(day)}
                        isPast={isPast(startOfDay(day)) && !isToday(day)}
                        scheduledTaskIds={scheduledTaskIds}
                    />
                ))}
            </Box>

            {/* Date Range Picker Modal */}
            <DateRangePicker
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                onDateRangeChange={(start, end) => {
                    onDateRangeChange(start, end);
                    setDatePickerOpen(false);
                }}
                selectedDate={selectedDate}
            />
        </Paper>
    );
};

export default WorkPlannerCalendar;